import { Flex, Stack, VStack } from '@chakra-ui/layout';
import {
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { PERMISSIONS as providerPermissions } from '@duke-hq/svc-iam-policy/provider/permissions';
import { RouterOutputs, trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function ProviderConsultantPermissions({
  providerId,
  member,
}: {
  providerId: string | number;
  member: NonNullable<RouterOutputs['providers']['user']['consultantById']>;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = trpcAdmin.providers.user.getPermissions.useQuery({
    providerId: Number(providerId),
    memberId: member.id,
  });

  return (
    <VStack p={4} gap={4}>
      <InputGroup alignItems="center">
        <InputLeftElement h="full">
          <Icon as={Search} color="fg.muted" size="20px" />
        </InputLeftElement>
        <Input
          size="sm"
          ml={1}
          variant="outline"
          placeholder="Find permissions"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>
      <Stack borderWidth="1px" borderColor="border.emphasis" w="100%" rounded="md" gap={0}>
        {isLoading === false &&
          Object.entries(providerPermissions)
            .filter(([_, permission]) =>
              permission.label.toLowerCase().includes(searchQuery.toLowerCase()),
            )
            .map(([id, permission]) => (
              <ProviderConsultantPermission
                key={id}
                id={id}
                permission={permission}
                providerId={providerId}
                member={member}
                initialState={data?.permissions.includes(id) ?? false}
              />
            ))}
      </Stack>
    </VStack>
  );
}

function ProviderConsultantPermission({
  providerId,
  member,
  id,
  permission,
  initialState,
}: {
  providerId: string | number;
  member: NonNullable<RouterOutputs['providers']['user']['consultantById']>;
  id: string;
  permission: (typeof providerPermissions)[keyof typeof providerPermissions];
  initialState: boolean;
}) {
  const toast = useToast();
  const [isEnabled, setIsEnabled] = useState<boolean>(initialState);

  const editProviderUser = trpcAdmin.providers.user.updatePermission.useMutation({
    onSettled: (data, error) => {
      if (error?.message != null) {
        toast({
          title: error.message,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
      } else {
        setIsEnabled(data != null || false);
        toast({
          title: 'Permission updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    },
  });

  return (
    <FormControl
      as={Flex}
      key={id}
      px={4}
      py={4}
      alignItems="center"
      justifyContent="space-between"
      _notLast={{
        borderBottomWidth: 1,
        borderBottomColor: 'border.emphasis',
      }}
    >
      <FormLabel px={0} mt="0" mb="0" fontWeight="medium">
        {permission.label}
      </FormLabel>
      <Switch
        colorScheme="brand"
        isDisabled={editProviderUser.isPending}
        onChange={(e) => {
          editProviderUser.mutate({
            providerId: Number(providerId),
            memberId: member.id,
            permissionId: id,
            enabled: e.target.checked,
          });
        }}
        defaultChecked={initialState}
        isChecked={isEnabled}
      />
    </FormControl>
  );
}
